/**
 *
 * ListCompanies
 *
 */

import { useMemo } from 'react';
import { Box, Button, Flex, Heading, HStack, IconButton, Spinner, SystemStyleObject } from '@chakra-ui/react';
import { useQuery } from '@tanstack/react-query';
import { TableEditIcon } from 'assets/icons';
import { AxiosError } from 'axios';
import { ROUTES } from 'config/routes';
import { Controller, useForm } from 'react-hook-form';
import { MdArrowDropDown } from 'react-icons/md';
import { useNavigate } from 'react-router-dom';
import CustomPagination from 'app/components/Pagination';
import Select, { IOption } from 'app/components/Select';
import Table, { IColumn, IRow } from 'app/components/Table';
import { useDebounce } from 'hooks/useDebounce';
import { useSession } from 'hooks/useSession';
import { useCustomToast } from 'hooks/useToast';
import {
	getAllCities,
	getAllCompleteCompanies,
	ICities,
	ICompaniesParams,
	IPaginatedCompanies,
} from 'services/http/company';
import { formatCep } from 'utils/formatCep';
import { handleDisableSelect } from 'utils/handlePermissions';
import { parseErrors, ResponseErrors } from 'utils/parseErrors';
import { parsedOptionArray } from 'utils/parseOptionArray';

interface IForm {
	city?: IOption;
	page_size?: number;
	page?: number;
}

const ListCompanies = () => {
	const styles: Record<string, SystemStyleObject> = {
		container: {
			width: '100%',
			padding: { lg: '4.5rem', base: '1rem' },
			boxShadow: 'rgba(99, 99, 99, 0.2) 0px 0.125rem 0.5rem 0px',
			backgroundColor: 'white',
			borderRadius: '0.5rem',
		},
		header: {
			w: '100%',
			alignItems: 'baseline',
			justifyContent: 'space-between',
			gap: '1.5rem',
			flexDirection: {
				base: 'column',
				md: 'row',
			},
		},
		containerButton: {
			marginLeft: 'auto',
			width: { base: '100%', md: 'fit-content' },
		},
		addButton: {
			h: '4rem',
			fontSize: { base: '1rem', md: '1.375rem' },
			fontWeight: 400,
			px: '2rem',
			letterSpacing: '0.063rem',
			w: { base: '100%', md: 'fit-content' },
		},
		heading: {
			fontSize: '1.87rem',
			lineHeight: '3.25rem',
			color: '#000',
			fontWeight: 'semibold',
		},
		filter: {
			m: {
				base: '1rem 0',
				md: '2.5rem 0',
			},
		},
		filterText: {
			color: 'blue.500',
			fontWeight: 600,
			fontSize: '1.375rem',
		},
		filterFields: {
			display: 'flex',
			flexWrap: 'wrap',
			mt: '1.25rem',
			gap: '3.125rem',
			flexDirection: {
				base: 'column',
				xl: 'row',
			},
			alignItems: { xl: 'flex-end', base: 'flex-start' },
		},
		select: {
			w: '100%',
			flex: 1,
			maxW: {
				base: '100%',
				xl: '23.5rem',
			},
		},
		selectLeft: {
			flex: 1,
			w: {
				base: '100%',
				xl: '15.25rem',
			},
		},
		button: {
			borderRadius: 0,
			width: '2.1875rem',
			height: '2.1875rem',
			display: 'flex',
			alignItems: 'center',
			justifyContent: 'center',
			_hover: {
				filter: 'brightness(0.8)',
			},
		},
		spinner: {
			boxSize: 4,
			color: 'white',
		},
		pagination: {
			w: '100%',
			justifyContent: 'end',
			mt: '2.5rem',
		},
		spinnerBox: {
			justifyContent: 'center',
		},
		grid: {
			justifyContent: 'center',
		},
		strippedCell: {
			backgroundColor: '#F2F2F2',
		},
		cell: {
			backgroundColor: '#ffffff',
		},
		boxSelectLeft: {
			w: { base: '100%', xl: 'fit-content' },
			gap: '2rem',
		},
	};

	const navigate = useNavigate();
	const { addToast } = useCustomToast();

	const {
		session: { user },
	} = useSession();

	const isSelectDisabled = handleDisableSelect(user);

	const { data: cities, isLoading: isCitiesLoading } = useQuery<ICities, AxiosError<ResponseErrors>, ICities>(
		['cities'],
		() => getAllCities(),
		{
			onError: errors => addToast({ type: 'error', description: parseErrors(errors?.response?.data) }),
		},
	);

	const allOptions = useMemo(
		() => [{ value: undefined, label: 'Todos' }, ...parsedOptionArray(cities?.cities || [])],
		[cities],
	);

	const defaultValues = {
		page: 1,
		page_size: 10,
		city: allOptions[0],
	};

	const { control, setValue, handleSubmit, getValues, watch } = useForm<IForm>({
		defaultValues,
	});

	const formValues = watch();

	const {
		data: companies,
		isLoading: companiesIsLoading,
		refetch: refetchListCompanies,
	} = useQuery<IPaginatedCompanies, AxiosError<ResponseErrors>, IPaginatedCompanies>(
		['companies', formValues],
		() => {
			const params: ICompaniesParams = {
				city: formValues?.city?.value ? String(formValues?.city?.value) : '',
				page: formValues?.page,
				page_size: formValues?.page_size,
			};

			return getAllCompleteCompanies(params);
		},
		{
			onError: errors =>
				addToast({
					type: 'error',
					title: 'Erro!',
					description: parseErrors(errors?.response?.data),
				}),
		},
	);

	const columns: IColumn[] = [
		{
			label: 'Unidade',
			key: 'unity',
		},
		{
			label: 'Município',
			key: 'city',
		},
		{
			label: 'Endereço ',
			key: 'adress',
		},
		{
			label: 'Ações',
			key: 'actions',
		},
	];

	const rows: IRow[] = useMemo(
		() =>
			companies?.results?.map(unity => {
				return {
					unity: unity?.company || '',
					city: unity?.city || '',
					adress:
						unity?.street && unity?.number
							? `${unity?.street || ''}, ${unity?.number || ''} - ${unity?.neighborhood || ''}, ${
									unity?.city || ''
							  } - ${unity?.state || ''}, ${formatCep(unity?.postalCode || '')}`.replace(/(, -|, ,| - ,| -$| ,$)/g, '')
							: '',
					actions: (
						<Flex sx={styles?.grid}>
							<Box>
								<IconButton
									data-testid={`button--edit--${unity.id}`}
									aria-label={`Editar unidade ${unity.id}`}
									icon={<TableEditIcon />}
									background="yellow.500"
									sx={styles?.button}
									onClick={() => navigate(ROUTES.editCompany(String(unity.id!)))}
								/>
							</Box>
						</Flex>
					),
				};
			}) || [],
		[companies, navigate, styles?.button, styles?.grid],
	);

	const onFilter = (page: number) => {
		setValue('page', page);
		handleSubmit(onSubmit)();
	};

	const debounced = useDebounce(() => onFilter(1));

	const onSubmit = () => refetchListCompanies();

	return (
		<>
			<Box p={{ lg: '3.75rem', base: '1rem' }}>
				<Box sx={styles.container}>
					<HStack sx={styles?.header}>
						<Heading sx={styles.heading}>Unidades</Heading>
					</HStack>

					{isCitiesLoading ? (
						<Flex sx={styles.spinnerBox}>
							<Spinner />
						</Flex>
					) : (
						<>
							<Box sx={styles?.filter} as="form" onSubmit={handleSubmit(onSubmit)}>
								<Box sx={styles?.filterFields}>
									<Box sx={styles?.select}>
										<Controller
											control={control}
											name="city"
											render={({ field }) => (
												<Select
													dropdownIndicatorComponent={MdArrowDropDown}
													label="Cidade"
													placeholder="Selecione uma cidade"
													options={allOptions}
													isLoading={isCitiesLoading}
													onChange={event => {
														setValue('page', 1);
														setValue('city', event as IOption);
														debounced();
													}}
													value={field.value}
													dataTestId="input--city"
													isDisabled={isSelectDisabled}
												/>
											)}
										/>
									</Box>

									<Box sx={styles.containerButton}>
										<Button
											sx={styles.addButton}
											data-testid="button--register"
											onClick={() => navigate(ROUTES.registerCompany)}
										>
											Cadastrar Nova Unidade
										</Button>
									</Box>
								</Box>
							</Box>

							{companiesIsLoading ? (
								<Flex sx={styles.spinnerBox}>
									<Spinner />
								</Flex>
							) : (
								<Table
									striped
									columns={columns}
									data={rows}
									cellStripedCustomStyle={styles.strippedCell}
									cellCustomStyle={styles.cell}
									headerCustomStyle={{ backgroundColor: '#173569' }}
								/>
							)}
						</>
					)}

					<Flex sx={styles.pagination}>
						<CustomPagination
							currentPage={Number(getValues('page'))}
							totalItems={companies?.totalRecords}
							pageSize={Number(defaultValues?.page_size)}
							onPaginate={onFilter}
						/>
					</Flex>
				</Box>
			</Box>
		</>
	);
};

export default ListCompanies;
